import {
  type DecodedHistorySnapshotBundle,
  decodeHistorySnapshotBundle,
  type EncodedHistorySnapshotBundle,
  useReadonlySnapshotPageFromBundle,
} from '@affine/core/components/affine/page-history-modal/data';
import { ReadonlyDocSnapshotPreview } from '@affine/core/components/affine/page-history-modal/readonly-doc-snapshot-preview';
import { useLocalePicker } from '@affine/core/hooks/use-locale-picker';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { useService } from '@toeverything/infra';
import { useEffect, useState } from 'react';

import * as styles from './styles.css';
import type { MarketplaceTemplate } from './use-marketplace-api';

type PreviewState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ready'; bundle: DecodedHistorySnapshotBundle }
  | { kind: 'empty'; message: string }
  | { kind: 'error'; message: string };

export function useMarketplaceDocTemplatePreview(
  template: MarketplaceTemplate,
  workspaceId: string,
  targetVersionNumber?: number
): PreviewState {
  const [state, setState] = useState<PreviewState>({ kind: 'idle' });

  useEffect(() => {
    if (template.templateType !== 'doc' && template.templateType !== 'app') {
      setState({ kind: 'empty', message: 'Document preview not available.' });
      return;
    }

    const controller = new AbortController();
    setState({ kind: 'loading' });

    const url = new URL(
      `/api/workspace/${workspaceId}/marketplace/templates/${template.id}/doc-preview`,
      window.location.origin
    );
    if (targetVersionNumber != null) {
      url.searchParams.set('targetVersionNumber', String(targetVersionNumber));
    }

    fetch(url.toString(), {
      credentials: 'include',
      signal: controller.signal,
    })
      .then(async res => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(
            typeof body?.message === 'string'
              ? body.message
              : `Failed to load document preview: ${res.status}`
          );
        }
        const contentType = res.headers.get('content-type') ?? '';
        const bundle = contentType.includes('application/json')
          ? decodeHistorySnapshotBundle(
              (await res.json()) as EncodedHistorySnapshotBundle
            )
          : {
              page: {
                docId: `marketplace-doc-template-${template.id}`,
                snapshot: await res.arrayBuffer(),
              },
              systemDocs: [],
              warnings: [
                'Preview endpoint returned the legacy single-document payload.',
              ],
            };
        if (!bundle.page.docId || bundle.page.snapshot.byteLength === 0) {
          setState({ kind: 'empty', message: 'Document preview is empty.' });
          return;
        }
        setState({ kind: 'ready', bundle });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setState({
          kind: 'error',
          message:
            err instanceof Error
              ? err.message
              : 'Failed to load document preview.',
        });
      });

    return () => controller.abort();
  }, [targetVersionNumber, template.id, template.templateType, workspaceId]);

  return state;
}

export function DocTemplatePreviewPanel({
  template,
  targetVersionNumber,
  workspaceId,
}: {
  template: MarketplaceTemplate;
  targetVersionNumber?: number;
  workspaceId: string;
}) {
  if (template.templateType === 'app_block') {
    return <AppBlockTemplatePreviewPanel template={template} />;
  }
  return (
    <DocumentTemplatePreviewPanel
      template={template}
      targetVersionNumber={targetVersionNumber}
      workspaceId={workspaceId}
    />
  );
}

function AppBlockTemplatePreviewPanel({
  template,
}: {
  template: MarketplaceTemplate;
}) {
  const picker = useLocalePicker();
  const title = picker?.pickTitle(template.titlesI18n) ?? 'App-Block';
  return (
    <section className={styles.docTemplatePreviewPanel}>
      <div className={styles.docTemplatePreviewState}>
        <div className={styles.detailSectionTitle}>{title}</div>
        <p>
          App Blocks are previewed inside the target app during route setup.
        </p>
      </div>
    </section>
  );
}

function DocumentTemplatePreviewPanel({
  template,
  targetVersionNumber,
  workspaceId,
}: {
  template: MarketplaceTemplate;
  targetVersionNumber?: number;
  workspaceId: string;
}) {
  const workspace = useService(WorkspaceService).workspace;
  const previewState = useMarketplaceDocTemplatePreview(
    template,
    workspaceId,
    targetVersionNumber
  );
  const previewScopeKey = `marketplace:${template.id}:v${
    targetVersionNumber ??
    template.versions?.[0]?.versionNumber ??
    template.installedVersionNumber ??
    template.updatedAt
  }`;
  const snapshotPage = useReadonlySnapshotPageFromBundle(
    workspace.docCollection,
    previewState.kind === 'ready' ? previewState.bundle.page.docId : undefined,
    previewState.kind === 'ready' ? previewState.bundle : undefined,
    previewScopeKey
  );

  if (
    template.templateType !== 'doc' &&
    template.templateType !== 'app' &&
    template.templateType !== 'app_block'
  ) {
    return null;
  }

  return (
    <section className={styles.docTemplatePreviewPanel}>
      {previewState.kind === 'error' || previewState.kind === 'empty' ? (
        <div className={styles.docTemplatePreviewState}>
          <div className={styles.detailSectionTitle}>Document preview</div>
          <p>{previewState.message}</p>
        </div>
      ) : (
        <ReadonlyDocSnapshotPreview
          mode="edgeless"
          onModeChange={() => {}}
          snapshotPage={snapshotPage}
          title={null}
          hideHeader
          fitEdgelessOnReady
          style={{ height: 360 }}
        />
      )}
    </section>
  );
}
